import { get } from "lodash-es";
import moment from "moment";
import {
	MESApiService,
	type spParameter,
} from "../../../../services/ApiService";
import type {
	ClientTelemetry,
	ClientTelemetryEvent,
} from "../models/ClientTelemetry";

type ClientTelemetryParams = {
	startDate: Date;
	endDate: Date | null;
	search?: string;
	userId?: number | null;
};

type ClientTelemetryEventsParams = ClientTelemetryParams & {
	clientId?: string | null;
	eventType?: string | null;
};

const toNullableText = (value?: string | null) => {
	const trimmed = value?.trim();
	return trimmed ? trimmed : null;
};

const toEndDate = (endDate: Date | null) => {
	if (endDate) {
		return endDate;
	}

	return moment().hour(23).minute(59).second(59).toDate();
};

export const getClientTelemetry = async (
	params: ClientTelemetryParams,
	signal?: AbortSignal,
) => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [
		{ name: "Start", value: params.startDate },
		{ name: "End", value: toEndDate(params.endDate) },
		{ name: "UserId", value: params.userId ?? null },
		{ name: "Username", value: null },
		{ name: "Search", value: toNullableText(params.search) },
	];

	const resp = await apiService.callV2(
		"[MES].[GetClientTelemetry]",
		parameters,
		signal,
	);

	if (resp.ok) {
		return get(resp, "data.tables[0].rows", []).map((row: ClientTelemetry) => ({
			...row,
			FirstSeenAt: row.FirstSeenAt
				? moment.utc(row.FirstSeenAt).toDate()
				: null,
			LastSeenAt: row.LastSeenAt ? moment.utc(row.LastSeenAt).toDate() : null,
			LastRouteChangedAt: row.LastRouteChangedAt
				? moment.utc(row.LastRouteChangedAt).toDate()
				: null,
		})) as ClientTelemetry[];
	}

	throw new Error(resp.message || "Error fetching client telemetry");
};

export const getClientTelemetryEvents = async (
	params: ClientTelemetryEventsParams,
	signal?: AbortSignal,
) => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [
		{ name: "Start", value: params.startDate },
		{ name: "End", value: toEndDate(params.endDate) },
		{ name: "ClientId", value: params.clientId ?? null },
		{ name: "UserId", value: params.userId ?? null },
		{ name: "EventType", value: toNullableText(params.eventType) },
		{ name: "Search", value: toNullableText(params.search) },
	];

	const resp = await apiService.callV2(
		"[MES].[GetClientTelemetryEvents]",
		parameters,
		signal,
	);

	if (resp.ok) {
		return get(resp, "data.tables[0].rows", []).map(
			(row: ClientTelemetryEvent) => ({
				...row,
				EventTimestamp: row.EventTimestamp
					? moment.utc(row.EventTimestamp).toDate()
					: null,
			}),
		) as ClientTelemetryEvent[];
	}

	throw new Error(resp.message || "Error fetching client telemetry events");
};
